import { useRef, useState } from "react";

// How many horizontal pixels of drag equal one `effectiveStep` increment —
// small enough for responsive-feeling dragging, large enough that a
// double-click's two near-zero-movement clicks never register as a drag.
const PIXELS_PER_STEP = 4;
const DRAG_THRESHOLD_PX = 3;

function formatValue(value) {
  if (!Number.isFinite(value)) return "0";
  // Rounds away float noise (e.g. 1.2000000000000002) without hardcoding a
  // fixed decimal count, so "1" still reads as "1", not "1.000".
  return String(Math.round(value * 1000) / 1000);
}

/**
 * @input {value: 1, step: 1}
 * @output <div>1</div> — drag horizontally to change; double-click to type a value
 */
export default function DragNumberInput({
  id,
  value,
  onChange,
  step = 1,
  snapValue = 5,
  fineValue = 0.1,
  className = "",
}) {
  const [editing, setEditing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [draft, setDraft] = useState("");
  const dragRef = useRef(null); // {startX, startValue, dragging} while a drag is in progress
  const finishedRef = useRef(false); // guards onBlur from double-handling after Enter/Escape already did

  const startEditing = () => {
    finishedRef.current = false;
    setDraft(formatValue(value));
    setEditing(true);
  };

  const finishEditing = (commit) => {
    finishedRef.current = true;
    if (commit) {
      const trimmed = draft.trim();
      const num = Number(trimmed);
      if (trimmed !== "" && Number.isFinite(num)) onChange(num);
    }
    setEditing(false);
  };

  const handlePointerDown = (e) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startValue: value };
  };

  const handlePointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const deltaX = e.clientX - drag.startX;
    if (!dragging) {
      if (Math.abs(deltaX) < DRAG_THRESHOLD_PX) return;
      setDragging(true);
    }
    const effectiveStep =
      e.ctrlKey || e.metaKey ? fineValue : e.shiftKey ? snapValue : step;
    const steps = Math.round(deltaX / PIXELS_PER_STEP);
    onChange(drag.startValue + steps * effectiveStep);
  };

  const handlePointerUp = (e) => {
    if (dragRef.current) e.currentTarget.releasePointerCapture(e.pointerId);
    dragRef.current = null;
    setDragging(false);
  };

  if (editing) {
    return (
      <input
        id={id}
        autoFocus
        type="text"
        inputMode="decimal"
        value={draft}
        onFocus={(e) => e.target.select()}
        onChange={(e) => setDraft(e.target.value.replace(/[^0-9.\-]/g, ""))}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") {
            e.preventDefault();
            finishEditing(true);
          } else if (e.key === "Escape") {
            e.preventDefault();
            finishEditing(false);
          }
        }}
        onBlur={() => {
          if (!finishedRef.current) finishEditing(false);
        }}
        className={`text-xs h-7 px-1.5 w-full text-center rounded-md border border-primary/50 bg-secondary/30 outline-none ${className}`}
      />
    );
  }

  return (
    <div
      id={id}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onDoubleClick={startEditing}
      title="Drag to change — hold Shift to snap, Ctrl/Cmd for finer steps. Double-click to type a value."
      className={`text-xs h-7 px-1.5 w-full text-center rounded-md border select-none cursor-ew-resize flex items-center justify-center tabular-nums
        ${dragging ? "border-primary/60 bg-secondary/60" : "border-secondary-foreground/20 bg-secondary/30"}
        ${className}`}>
      {formatValue(value)}
    </div>
  );
}
